import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const MapLegend = ({ isH3Visible, h3ActiveView, categories = {} }) => {
    // Start minimized on both mobile and desktop
    const [isMinimized, setIsMinimized] = useState(true);

    // Update minimized state on window resize
    useEffect(() => {
        const handleResize = () => {
            // Only auto-minimize/expand if user hasn't manually toggled
            // This prevents annoying auto-changes during resize
            const isMobile = window.innerWidth <= 768;
            if (isMobile && !isMinimized) {
                // Don't auto-minimize on mobile if user expanded it
                return;
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMinimized]);

    if (isMinimized) {
        return (
            <button
                onClick={() => setIsMinimized(false)}
                className="absolute bottom-20 md:bottom-4 left-4 bg-white rounded-lg shadow-xl px-4 py-2 z-10 border border-gray-200 flex items-center gap-2 hover:shadow-2xl transition-shadow"
            >
                <ChevronUp className="w-4 h-4" />
                <span className="font-semibold text-sm">Legend</span>
            </button>
        );
    }

    return (
        <div className="absolute bottom-28 md:bottom-16 left-4 bg-white rounded-xl shadow-2xl p-4 z-10 border border-gray-200 min-w-[200px] max-h-[70%] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-sm text-gray-900">Map Legend</h4>
                <button
                    onClick={() => setIsMinimized(true)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                    <ChevronDown className="w-4 h-4" />
                </button>
            </div>

            {/* H3 Layer Legend */}
            {isH3Visible && (
                <div className="space-y-2 mb-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase">
                        {h3ActiveView === 'sales' ? 'Sales Data'
                            : h3ActiveView === 'revenue' ? 'Transaction Revenue'
                            : 'Traffic Density'}
                    </div>

                    {h3ActiveView === 'revenue' ? (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(255, 236, 179, 0.75)' }}></div>
                                <span className="text-xs text-gray-700">Lowest revenue</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(255, 213, 128, 0.75)' }}></div>
                                <span className="text-xs text-gray-700">Low</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(255, 183, 77, 0.75)' }}></div>
                                <span className="text-xs text-gray-700">Medium</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(251, 140, 0, 0.75)' }}></div>
                                <span className="text-xs text-gray-700">High</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(230, 81, 0, 0.75)' }}></div>
                                <span className="text-xs text-gray-700">Highest revenue</span>
                            </div>
                            <div className="text-[10px] text-gray-500 pt-1">
                                Closed-deal revenue per area (zoom in for top product)
                            </div>
                        </div>
                    ) : h3ActiveView === 'sales' ? (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(165, 242, 243, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">¥0 - 100M</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(144, 224, 239, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">¥100M - 500M</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(100, 181, 246, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">¥500M - 1B</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(66, 165, 245, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">¥1B - 2B</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(33, 150, 243, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">¥2B+</span>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(227, 242, 253, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">Very Low</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(187, 222, 251, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">Low</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(144, 202, 249, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">Medium</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(100, 181, 246, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">High</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(66, 165, 245, 0.7)' }}></div>
                                <span className="text-xs text-gray-700">Very High</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Entity Status Legend */}
            <div className="border-t border-gray-200 pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-3 tracking-wider">Entity Status</div>

                {/* Lead Statuses */}
                <div className="mb-3">
                    <div className="text-[9px] font-bold text-gray-400 mb-1.5 tracking-widest uppercase">LEADS</div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-blue-500 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-700">New</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-purple-600 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-700">Planned</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-sky-400 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-700">Contacted</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-green-600 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-700">Won</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-gray-400 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-700">Lost</span>
                        </div>
                    </div>
                </div>

                {/* Customer Statuses */}
                <div className="mb-3">
                    <div className="text-[9px] font-bold text-gray-400 mb-1.5 tracking-widest uppercase">CUSTOMERS</div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-700">Active</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-orange-500 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-700">At Risk</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-gray-400 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-700">Inactive</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-700">Churned</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Priority */}
            <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Priority</div>
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm">🔥🔥🔥</span>
                        <span className="text-xs text-gray-700">High Priority</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm">⚡⚡</span>
                        <span className="text-xs text-gray-700">Medium Priority</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm">❄️</span>
                        <span className="text-xs text-gray-700">Low Priority</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MapLegend;
